import React, { useCallback, useEffect, useState } from 'react';
import { Edit2, Plus, RefreshCw, RotateCcw, Trash2, X } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import RowActionsMenu from '../RowActionsMenu';
import TablePager, { usePagedRows } from '../TablePager';

// The kinds of leave and how many days a year each one allows. Paid types are paid in full by the
// payroll; an unpaid type is what someone is offered when a paid quota runs out, so there must
// always be at least one unpaid type.

const TypeModal = ({ existing, onClose, onSaved }) => {
  const notify = useToast();
  const [name, setName] = useState(existing?.name || '');
  const [quota, setQuota] = useState(existing?.annualQuota != null ? String(existing.annualQuota) : '');
  const [paid, setPaid] = useState(existing ? existing.paid !== false : true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useDialog(() => { if (!saving) onClose(); });

  const submit = async () => {
    if (!name.trim()) { setError('Name the leave type.'); return; }
    const q = quota.trim() === '' ? null : Number(quota);
    if (q != null && (Number.isNaN(q) || q < 0 || q > 365)) { setError('The yearly days must be between 0 and 365, or empty for no limit.'); return; }
    setError('');
    setSaving(true);
    const body = { name: name.trim(), annualQuota: q, paid };
    try {
      const saved = existing
        ? await attendanceAPI.updateLeaveType(existing.id, body)
        : await attendanceAPI.createLeaveType(body);
      notify('success', existing ? 'Leave type updated' : 'Leave type added');
      onSaved(saved);
    } catch (e) {
      setError(e?.message || 'The leave type could not be saved.');
      setSaving(false);
    }
  };

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label={existing ? 'Edit leave type' : 'Add leave type'}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              {existing ? <Edit2 className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
            </div>
            <h2 className="text-lg font-bold text-white">{existing ? 'Edit leave type' : 'Add leave type'}</h2>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} disabled={saving} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label htmlFor="type-name" className="block text-sm font-semibold mb-1">Name <span className="text-red-500">*</span></label>
            <input
              id="type-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              disabled={saving}
              autoFocus
              placeholder="e.g. Casual"
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label htmlFor="type-quota" className="block text-sm font-semibold mb-1">Days a year</label>
            <input
              id="type-quota"
              type="number"
              min="0"
              max="365"
              step="0.5"
              value={quota}
              onChange={(e) => setQuota(e.target.value)}
              disabled={saving}
              placeholder="Leave empty for no limit"
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
            <p className="mt-1 text-xs text-gray-500">Empty means there is no yearly limit, which is how unpaid leave usually works.</p>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={paid}
              onChange={(e) => setPaid(e.target.checked)}
              disabled={saving}
              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span>Paid leave (salary is not deducted for these days)</span>
          </label>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : existing ? 'Save type' : 'Add type'}
          </button>
        </div>
      </div>
    </div>
  );
};

const LeaveTypesSettings = ({ canEdit }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const res = await attendanceAPI.leaveTypes(canEdit);
      setRows(Array.isArray(res) ? res : []);
      setError(null);
    } catch (e) {
      setError(e?.message || 'The leave types could not be loaded.');
    }
  }, [canEdit]);
  useEffect(() => { load(); }, [load]);

  const setActive = async (type, active) => {
    try {
      await attendanceAPI.updateLeaveType(type.id, { active });
      notify('success', active ? `${type.name} is available again` : `${type.name} is no longer offered`);
      load();
    } catch (e) {
      notify('error', e?.message || 'The leave type could not be changed.');
    }
  };

  const remove = async (type) => {
    const ok = await confirm({
      title: `Delete "${type.name}"?`,
      message: 'This only works if nobody has ever asked for this type. Otherwise switch it off instead, so the old requests keep their name.',
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await attendanceAPI.deleteLeaveType(type.id);
      notify('success', 'Leave type deleted');
      load();
    } catch (e) {
      notify('error', e?.code === 'TYPE_IN_USE'
        ? 'People have already asked for this type, so it cannot be deleted. Switch it off instead.'
        : e?.message || 'The leave type could not be deleted.');
    }
  };

  const paging = usePagedRows(rows, { id: 'leave-types' });

  return (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Leave types</h2>
          <p className="text-sm text-gray-600">What staff can ask for, and how many days a year each one allows.</p>
        </div>
        {canEdit && (
          <button type="button" onClick={() => setEditing('new')} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
            <Plus className="w-4 h-4" aria-hidden="true" /> Add type
          </button>
        )}
      </div>

      {error ? (
        <p className="p-5 text-sm text-red-700">{error}</p>
      ) : !rows ? (
        <p className="p-10 text-center text-sm text-gray-500"><RefreshCw className="w-5 h-5 animate-spin inline mr-2" aria-hidden="true" />Loading…</p>
      ) : (
        <>
        {/* Square corners: the bar sits under the card's heading, not at its top edge. */}
        <TablePager paging={paging} noun="leave type" className="px-5" rounded={false} />
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <th className="px-5 py-3">Type</th>
              <th className="px-4 py-3">Days a year</th>
              <th className="px-4 py-3">Pay</th>
              <th className="px-4 py-3">Offered</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {paging.pageRows.map((t) => (
              <tr key={t.id} className={t.active === false ? 'opacity-60' : ''}>
                <td className="px-5 py-3 font-medium text-gray-900">{t.name}</td>
                <td className="px-4 py-3 text-gray-700">{t.annualQuota == null ? 'No limit' : t.annualQuota}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.paid === false ? 'bg-gray-100 text-gray-700' : 'bg-emerald-100 text-emerald-800'}`}>
                    {t.paid === false ? 'Unpaid' : 'Paid'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700">{t.active === false ? 'No' : 'Yes'}</td>
                <td className="px-4 py-3 text-right">
                  {canEdit ? (
                    <RowActionsMenu
                      items={[
                        { key: 'edit', label: 'Edit', icon: Edit2, iconClass: 'text-emerald-500', onClick: () => setEditing(t) },
                        t.active === false
                          ? { key: 'on', label: 'Offer again', icon: RotateCcw, iconClass: 'text-emerald-500', onClick: () => setActive(t, true) }
                          : { key: 'off', label: 'Stop offering', icon: Trash2, danger: true, onClick: () => setActive(t, false) },
                        { key: 'delete', label: 'Delete', icon: Trash2, danger: true, onClick: () => remove(t) },
                      ]}
                    />
                  ) : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager paging={paging} noun="leave type" position="bottom" className="px-5" />
        </>
      )}

      {editing && (
        <TypeModal
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

export default LeaveTypesSettings;
